import React, { Component, PureComponent } from "react";
import styles from "./styles.scss";
import CSSModules from "react-css-modules";
import PropTypes from "prop-types";
import { actions } from "./redux";
import { connect } from "react-redux";
import { KeyboardArrowDown, KeyboardArrowUp, ChevronLeft, ChevronRight } from "@material-ui/icons";
import { Button } from "@material-ui/core";
import { array, ManualPromise, shouldUpdate } from "../../utils/index";

/*

Use:  read propTypes / defaultProps.
      can be used as a purely responsive table or with full smart table functionality.
      there is just enough css for this component to make it work. it is up to the consumer to 
		style fully.
		use `index` prop on theads and tbodys to map them to each other. If no `index` specified, will draw them in 
		order given

         <SmartTable columns={3} smartTableId="homeListTable" data={medata}>
            <thead index={1}>
               <tr >
                  <th sortable="name"  default-sort="asc">name</th>
                  <th >no sort</th>
                  <th sortable="other">other title</th>
               </tr>
            </thead>
            <tbody  index={1}>
               {medata.map(x => {
                  return <tr>
                           <td>{x.name}</td>
                           <td>{x.whatever}</td>
                           <td>{x.other}</td>
                        </tr>
               })}

            </tbody>
      </SmartTable>

*/

class Table extends Component {
	static defaultProps = {
		data: [],
		rowsPerPage: 0, // zero for no pagination
		allowColumnSort: false,
		changeData: null, //func. required if allow column sort
		useResponsiveColumns: false,
		breakOn: "sm", // sm | md | xs | lg
		hideHeaderWhenCollapsed: false,
		columns: null, // num columns
		stickyHeaderScrollRef: null, //pass the ref to the parent scroll element to show sticky headers

		// styleName overrides. (styleNames put on child tr/td/th elements, where this component is used, are preserved;
		// some of these props are redundant for css wizards.)
		tableClass: "",
		collapsedTableHeadersClass: "", //class to be applied to div that contains responsive header title
		theadClass: "",
		tbodyClass: "",
		theadrowClass: "",
		tbodyrowClass: "",
		sortChevronClass: "",
		tdContentClass: "",
		thContentClass: "",
		thClass: "",
		tdClass: "",
		paginationTextClass: ""
	};
	static propTypes = {
		data: function(props, propName, componentName) {
			//required if sorting or paging (for sorting)
			const test = props.allowColumnSort || props.rowsPerPage > 0 ? PropTypes.array.isRequired : PropTypes.array;
			const result = test.apply(this, arguments);
			if (result) {
				result.message =
					"The prop `data` is required in `SmartTable` if `allowColumnSort` is set to true or `rowsPerPage` > 0 (paging enabled)";
			}
			return result;
		},
		smartTableId: PropTypes.string.isRequired,
		rowsPerPage: PropTypes.number.isRequired,
		breakOn: PropTypes.oneOf(["sm", "md", "xs", "lg"]).isRequired,
		columns: PropTypes.number, //not required if no paging or folding columns
		children: PropTypes.array.isRequired, //set as child elements, not as a prop like this -> children={...}
		changeData: function(props, propName, componentName) {
			//required if allowColumnSort
			const test = props.allowColumnSort ? PropTypes.func.isRequired : PropTypes.func;
			const result = test.apply(this, arguments);
			if (result) {
				result.message =
					"The prop `changeData` is required in `SmartTable` if `allowColumnSort` is set to true.";
			}
			return result;
		},
		classes: PropTypes.object //style overrides
	};

	constructor(props) {
		super(props);
		this.handleStickyScroll = this.handleStickyScroll.bind(this);

		this._hasStickyHeaders = !!this.props.stickyHeaderScrollRef;
		this.sortPropName = "_st_orig_sort_order_" + this.props.smartTableId;

		this.props.data.forEach((x, i) => {
			x[this.sortPropName] = i;
		});

		//apply styleName classes to these components
		this.SmartTableHead = CSSModules(
			SmartTableHead,
			{ ...styles, ...this.props.classes },
			{
				allowMultiple: true
			}
		);

		this.SmartTableBody = CSSModules(
			SmartTableBody,
			{ ...styles, ...this.props.classes },
			{
				allowMultiple: true
			}
		);

		this.Paging = CSSModules(
			SmartTablePaging,
			{ ...styles, ...this.props.classes },
			{
				allowMultiple: true
			}
		);

		let tables = [],
			els = [];

		//construct table config for each table within.
		//can have multiple 'sub tables' if collapsable cols is enabled
		//loop over all immediate SmartTable children
		React.Children.forEach(this.props.children, (childEl, i) => {
			if (childEl.type === "thead") {
				els.push({
					head: true,
					index: i,
					//table index for multi tables
					map: childEl.props.index,
					//a ref the thead element
					theadref: this._hasStickyHeaders ? React.createRef() : null,
					stickytheadref: this._hasStickyHeaders ? React.createRef() : null,
					//a ref to the all the first th in every column, per table
					headerColRefs: this._hasStickyHeaders ? array.fill(this.props.columns, x => React.createRef()) : [],
					stickyHeaderColRefs: this._hasStickyHeaders ? array.fill(this.props.columns, x => React.createRef()) : [],
					collapsable: false
				});
			}
			if (childEl.type === "tbody") {
				els.push({ index: i, map: childEl.props.index, collapsable: false });
			}
			if (childEl.type === CollapsableTableHead) {
				this._hasCollapsable = true;
				els.push({
					head: true,
					index: i,
					//table index for multi tables
					map: childEl.props.index,
					collapsable: true,
					scrollProps: childEl.props.scrollProps,
					//a ref to the all the first th in every column, per table
					headerColRefs: this._hasStickyHeaders ? array.fill(this.props.columns, x => React.createRef()) : [],
					stickyHeaderColRefs: this._hasStickyHeaders ? array.fill(this.props.columns, x => React.createRef()) : [],
					//a ref the thead element
					theadref: this._hasStickyHeaders ? React.createRef() : null,
					stickytheadref: this._hasStickyHeaders ? React.createRef() : null,
				});
			}
			if (childEl.type === CollapsableTableBody) {
				this._hasCollapsable = true;
				els.push({
					index: i,
					map: childEl.props.index,
					collapsable: true,
					scrollProps: childEl.props.scrollProps
				});
			}
		});

		const grouped = array.groupBy(els, x => x.map);

		//any sub elements that didnt specify index should be linked up in numerical order
		array.mapProps(grouped, (x, prop) => {
			if (prop !== "undefined" && prop !== "null") {
				const head = x.find(f => f.head);
				const body = x.find(f => !f.head);
				tables.push({ head, body, scrollProps: { ...body.scrollProps, ...head.scrollProps } });
			} else {
				const heads = x.filter(f => f.head);
				const bodys = x.filter(f => !f.head);

				while (heads.length || bodys.length) {
					tables.push({ head: heads.shift(), body: bodys.shift() });
				}
			}
		});

		this.refProms = [];
		this.tableDimension = new TableDimension();

		if (this._hasStickyHeaders) {
			const scroller = this.props.stickyHeaderScrollRef.current || this.props.stickyHeaderScrollRef;
			scroller.addEventListener("scroll", this.handleStickyScroll);
		}

		const sortcol = this.props.sortableColumns.find(x => x.sortDir !== 0);
		if (sortcol) {
			this.sortCol(sortcol, true);
		}

		this.state = {
			tables
		};
	}

	/**
	 * called on scroll when an element is passed to props.stickyHeaderScrollRef
	 *
	 * enables sticky header
	 */
	handleStickyScroll(e) {
		const tables = this.state.tables.filter(x => x.head.theadref && x.head.theadref.current && x.head.stickytheadref.current);

		for (let index = 0; index < tables.length; index++) {
			const table = tables[index];
			const thead = table.head.theadref.current;
			const box = thead.getBoundingClientRect();
			if (box.top <= 0) {
				table.head.stickytheadref.current.style.display = "inherit"
				if (!table.head.showingSticky){
					for (let index = 0; index < table.head.headerColRefs.length; index++) {
						//assign the real cols widths to each stich th. do this becuse floating them removes table col width
						const mainTh = table.head.headerColRefs[index];
						const stickyTh = table.head.stickyHeaderColRefs[index];
						if(stickyTh.current){
							stickyTh.current.style.width = mainTh.current.clientWidth + "px"
						}
					}
				}
				table.head.showingSticky = true;
			} else {
				table.head.showingSticky = false;
				table.head.stickytheadref.current.style.display = "none"
			}
		}

	}

	shouldComponentUpdate(nextProps, nextState) {
		//stop unessecary spam
		const update = shouldUpdate(this, nextProps, nextState, ["dataCount", "changeData"]);
		if (update) {
			if (nextProps.data.length > 0 && nextProps.data[0][this.sortPropName] === undefined) {
				nextProps.data.forEach((x, i) => {
					x[this.sortPropName] = i;
				});
			}
		}
		return update;
	}

	sortCol(sortableData, dontChangeOrder = false) {
		if (sortableData && this.props.allowColumnSort) {
			//the next time this component updates, dont resort in componentDidUpdate
			this._isSorting = true;
			this._currentSortedCol = sortableData;
			let dir = sortableData.sortDir;
			if (!dontChangeOrder) {
				dir = ((dir + 2) % 3) - 1;
			}
			this.props.setSortColumn(this.props.smartTableId, sortableData.th_Key, dir);

			//if we are gonna sort on server side do the call here
			//else sort the array here

			const propPath = sortableData.dataName.split(".");

			if (dir === 0) {
				//sort back to the orignal order
				const sorted = array.sort(this.props.data.slice(), x => x[this.sortPropName], true);
				this._currentSortedCol = null;
				this.props.changeData(sorted);
			} else {
				const sorted = array.sort(this.props.data.slice(), x => getValue(x, propPath), dir === -1);
				//pass sorted data back up to consumer component/redux etc
				this.props.changeData(sorted);
			}
		}
	}

	/**
	 * IF this table uses colapsable cols, then we need to get teh max height of the same
	 * row index for all the other tables. so rows all line up
	 */
	getHeight(row, existingStyle = {}) {
		if (this.tableDimension.rowHeights[row]) {
			existingStyle.height = this.tableDimension.rowHeights[row];
		}

		return existingStyle;
	}

	/**
	 *
	 * @param {Array} rows
	 * @param {Array} headers
	 * @param {number} tableIndex index of table when using collapsable columns
	 * @param {object} tblConfig item constructed in ctor, state.tables
	 * @param {bool} isSticky is facade sticky header
	 */
	renderHead(rows, headers, tableIndex, tblConfig, isSticky) {
		return React.Children.map(rows, (tr, key) => {
			if (!tr || !tr.props) {
				return null;
			}
			const { children, ...restProps } = tr.props;
			let prom = null;
			if (this._hasCollapsable) {
				//create  a promise to be resolved when the real dom renders
				prom = new ManualPromise();
				this.refProms.push(prom);
			}
			return (
				<tr
					{...restProps}
					key={key}
					styleName={`_theadrow _responsive-hide-header ${this.props.theadrowClass}`}
					ref={ref => {
						if (ref && this._hasCollapsable) {
							//coz refs arnt created until dom renders, create a promise to calculate el height
							prom.Resolve({
								height: ref.offsetHeight,
								key: "head_" + key
							});
						}
					}}
				>
					{React.Children.map(tr.props.children, (th, key2) => {
						const { children, onClick, style, ...restProps } = th.props;
						let newStyle = style || {};
						//get sort data to see if this col is sorted
						const sortableData = this.props.sortableColumns.find(x => x.th_Key === th.key);
						headers.add(th);
						//if this header is the sticky header and its <tr> index 0, then get the real headers' col widths
						//and assign them to this cols' top cell
						if (isSticky && key === 0 && tblConfig.head.headerColRefs[key2].current) {
							newStyle.width = tblConfig.head.headerColRefs[key2].current.clientWidth;
						}
						return (
							<th
								{...restProps}
								key={key2}
								onClick={e => {
									this.sortCol(sortableData);
									onClick && onClick(e);
								}}
								styleName={`_th ${this.props.thClass}`}
								issorted={sortableData && sortableData.sortDir !== 0 ? "true" : "false"}
								style={this.getHeight("head_" + key, newStyle)}
								ref={ref => {
									if (th.ref) {
										if (th.ref.current) {
											th.ref.current = ref;
										} else {
											//is function ref
											th.ref(ref);
										}
									}
									if (!isSticky && key === 0 && tblConfig.head.headerColRefs[key2]) {
										tblConfig.head.headerColRefs[key2].current = ref;
									}
									//if this header is the sticky header and its <tr> index 0, then get the real headers' col widths
									//and assign them to this cols' top cell
									if (isSticky && key === 0 && tblConfig.head.stickyHeaderColRefs[key2]) {
										tblConfig.head.stickyHeaderColRefs[key2].current = ref;
									}
								}}
							>
								<div styleName={`_th-content ${this.props.thContentClass}`}>
									{th.props.children}
									{sortableData &&
										sortableData.sortDir === 1 && (
											<KeyboardArrowUp styleName={this.props.sortChevronClass} key="up" />
										)}
									{sortableData &&
										sortableData.sortDir === -1 && (
											<KeyboardArrowDown styleName={this.props.sortChevronClass} key="down" />
										)}
								</div>
							</th>
						);
					})}
				</tr>
			);
		});
	}

	renderBody(rows, headers) {
		rows = React.Children.toArray(rows);

		//once server side paging is enabled, ignore this
		if (this.props.rowsPerPage > 0) {
			rows = rows.slice(
				this.props.rowsPerPage * this.props.currentPageNumber,
				this.props.rowsPerPage * this.props.currentPageNumber + this.props.rowsPerPage
			);
		}

		return rows.map((tr, key) => {
			if (!tr || !tr.props) {
				return null;
			}
			const { children, ...restProps } = tr.props;
			const last = rows.length === key + 1;
			const first = key === 0;
			if (headers && headers.length) {
				headers.reset();
			}
			let prom = null;
			if (this._hasCollapsable) {
				//a promise that resolves when the real dom renders
				prom = new ManualPromise();
				this.refProms.push(prom);
			}
			return (
				<tr
					st-first={first.toString()}
					st-last={last.toString()}
					{...restProps}
					key={key}
					styleName={`_tbodyrow ${this.props.tbodyrowClass}`}
					ref={ref => {
						if (ref && this._hasCollapsable) {
							//coz refs arnt created until dom renders, create a promise to calculate el height
							prom.Resolve({
								height: ref.offsetHeight,
								key: "body_" + key
							});
						}
					}}
				>
					{React.Children.map(tr.props.children, (td, key2) => {
						const { children, style, ...restProps } = td.props;

						const colspan = parseInt(td.props.colSpan || "1", 10);
						const header = headers && headers.length && headers.getHeader(colspan);

						return (
							<td
								{...restProps}
								key={key2}
								styleName={`_td ${this.props.tdClass}`}
								style={this.getHeight("body_" + key, style)}
								ref={td.ref}
							>
								<div styleName={`_td-content ${this.props.tdContentClass}`}>
									{this.props.useResponsiveColumns && (
										<div
											styleName={`_responsive-header ${this.props.collapsedTableHeadersClass}`}
											st-hide={this.props.hideHeaderWhenCollapsed.toString()}
										>
											{header && header.props.children}
										</div>
									)}
									{td.props.children}
								</div>
							</td>
						);
					})}
				</tr>
			);
		});
	}

	render() {
		const { children, className, ...restProps } = this.props;
		if (this.props.hasTable) {
			let tables = [];
			this.dataCount = this.props.data.length;

			const childrenArr = React.Children.toArray(this.props.children);
			let scrollTop = 0;
			if (this._hasStickyHeaders) {
				const scroller = this.props.stickyHeaderScrollRef.current || this.props.stickyHeaderScrollRef;
				scrollTop = scroller.getBoundingClientRect().top;
			}

			//unless collapsable cols is enabled, this list will always have 1 table
			for (let index = 0; index < this.state.tables.length; index++) {
				const tblData = this.state.tables[index];
				const headers = new HeaderList();

				const table = (
					<table
						key={index}
						styleName={`_table ${this.props.tableClass} ${
							this.props.useResponsiveColumns && !this._hasCollapsable ? "_responsive-table" : ""
						} ${this.props.breakOn}`}
					>
						{tblData.head &&
						!isNaN(tblData.head.index) && ( //has thead and tbody declared.
								<React.Fragment>
									<this.SmartTableHead
										{...restProps}
										key="head"
										className={childrenArr[tblData.head.index].props.className}
										headRef={tblData.head.theadref}
									>
										{this.renderHead(
											childrenArr[tblData.head.index].props.children,
											headers,
											index,
											tblData,
											false //not sticky
										)}
									</this.SmartTableHead>
									{/* create a copy of the real header as a sticky header */}
									{this._hasStickyHeaders && (
										<this.SmartTableHead
											{...restProps}
											key="stickyhead"
											isSticky={true}
											stickyTopPx={scrollTop}
											className={childrenArr[tblData.head.index].props.className}
											headRef={tblData.head.stickytheadref}
											>
											{this.renderHead(
												childrenArr[tblData.head.index].props.children,
												headers,
												index,
												tblData,
												true //sticky
											)}
										</this.SmartTableHead>
									)}
								</React.Fragment>
							)}
						<this.SmartTableBody
							{...restProps}
							hidePaging={this.state.tables.length > 1}
							paging={this.Paging}
							key="body"
							dataCount={this.dataCount}
							className={childrenArr[tblData.body.index].props.className}
						>
							{this.renderBody(childrenArr[tblData.body.index].props.children, headers, index)}
						</this.SmartTableBody>
					</table>
				);
				tables.push({ table, tblData });
			}

			//if tables > 1 then render those tables side by side. this creates collapsable cols with the appearence
			//of a single table
			return this.state.tables.length > 1 ? (
				<div className={this.props.className}>
					<div styleName="_collapse-wrap">
						{tables.map((x, i) => {
							const isScroll =
								(x.tblData.head && x.tblData.head.collapsable) || x.tblData.body.collapsable;
							return (
								<div key={i} st-scrollable={isScroll ? "true" : "false"} {...x.tblData.scrollProps}>
									{x.table}
								</div>
							);
						})}
					</div>
					{this.props.rowsPerPage > 0 && <this.Paging {...restProps} dataCount={this.dataCount} />}
				</div>
			) : (
				tables.map(x => x.table)
			);
		} else {
			return null;
		}
	}

	componentDidUpdate(prevProps) {
		if (prevProps.data !== this.props.data && this._currentSortedCol && !this._isSorting) {
			//if data was sorted and new data list came in, then sort that list
			this.sortCol(this._currentSortedCol, true);
		} else {
			this._isSorting = false;
		}

		if (this.refProms.length && this._hasCollapsable) {
			//get the hights of all tables' rows and adjust them so all rows line up
			if (this.refProms.length) {
				Promise.all(this.refProms.map(x => x.promise))
					.then(result => {
						//see if any rows changed height so we can update the others
						result = result.map(x =>
							this.tableDimension.addHeight({
								key: x.key,
								height: x.height
							})
						);
						if (this.tableDimension.next()) {
							//update with current row heights then clear after render
							//cant be done with setState so use forceRender. naughty
							this.refProms = [];
							this.forceUpdate(() => {
								setTimeout(() => {
									//clear proms to prevent inf loops
									this.refProms = [];
									//reset height data so rows can decrease in size if content adjusts
									this.tableDimension.reset();
								});
							});
						}
					})
					.catch(x => x);
			}
		}
	}

	componentWillUnmount() {
		if (this._hasStickyHeaders) {
			const scroller = this.props.stickyHeaderScrollRef.current || this.props.stickyHeaderScrollRef;
			scroller.removeEventListener("scroll", this.handleStickyScroll);
		}
	}
}

class SmartTablePaging extends Component {
	render() {
		const { rowsPerPage, currentPageNumber, dataCount } = this.props;
		const currentRow = rowsPerPage * currentPageNumber;
		const lastRowOfPage = Math.min(currentRow + rowsPerPage, dataCount);
		return (
			<div styleName="_pagination-wrap">
				<Button
					disabled={currentRow === 0}
					onClick={() =>
						this.props.changePage(this.props.smartTableId, -1, Math.ceil(dataCount / rowsPerPage))
					}
				>
					<ChevronLeft />
				</Button>
				<div styleName={`_pagination-text ${this.props.paginationTextClass}`}>
					Showing {Math.min(currentRow + 1, dataCount)} - {lastRowOfPage} of {dataCount}
				</div>
				<Button
					disabled={lastRowOfPage === dataCount}
					onClick={() =>
						this.props.changePage(this.props.smartTableId, 1, Math.ceil(dataCount / rowsPerPage))
					}
				>
					<ChevronRight />
				</Button>
			</div>
		);
	}
}

class SmartTableHead extends Component {
	static propTypes = {
		className: PropTypes.string,
		theadClass: PropTypes.string,
		isSticky: PropTypes.bool,
		stickyTopPx: PropTypes.number,
		headRef: PropTypes.exact({
			current: PropTypes.object
		})
	};
	render() {
		return (
			<thead
				className={this.props.className}
				styleName={`${this.props.theadClass} ${this.props.isSticky ? "_sticky-head" : ""}`}
				style={{ top: this.props.stickyTopPx }}
				ref={this.props.headRef}
			>
				{this.props.children}
			</thead>
		);
	}
}
class SmartTableBody extends Component {
	render() {
		return (
			<tbody className={this.props.className} styleName={this.props.tbodyClass}>
				{this.props.children}

				{this.props.rowsPerPage > 0 &&
					!this.props.hidePaging && (
						<tr>
							<td colSpan={this.props.columns}>
								<this.props.paging {...this.props} />
							</td>
						</tr>
					)}
			</tbody>
		);
	}
}

/**
 * fold columns like in excel.
	disables responsive columns.
	Any items in `scrollProps` prop will be spread to the scroll div
 */
export class CollapsableTableHead extends Component {
	static defaultProps = {
		index: null
	};
	static propTypes = {
		scrollProps: PropTypes.object
	};
}
/**
 * fold columns like in excel.
	disables responsive columns.
	Any items in `scrollProps` prop will be spread to the scroll div
 */
export class CollapsableTableBody extends Component {
	static defaultProps = {
		index: null
	};
	static propTypes = {
		scrollProps: PropTypes.object
	};
}

//register table in parent HoC so we dont load the table until it is registered
class SmartTable extends PureComponent {
	constructor(props) {
		super(props);
		this.state = {
			wrapped: null
		};
	}

	componentWillMount() {
		if (!this.props.smartTableId) {
			throw new Error("SmartTable must have 'smartTableId' prop set.");
		}

		this.props.registerTable(this.props.smartTableId, this.props.children, this.props.rowsPerPage);

		const bindAction = dispatch => {
			return {
				setSortColumn: (tableId, thKey, direction) =>
					dispatch(actions.setSortColumn(tableId, thKey, direction)),
				changePage: (tableId, increment, totalPages) =>
					dispatch(actions.changePage(tableId, increment, totalPages)),
				updateTable: (tableId, data) => dispatch(actions.updateTable(tableId, data))
			};
		};

		//only map the props for this table id
		const mapStateToProps = state => {
			const table = state.smartTable.tables[this.props.smartTableId];
			const { tables, ...rest } = state.smartTable;
			return {
				...rest,
				...table
			};
		};

		this.setState({
			wrapped: connect(
				mapStateToProps,
				bindAction
			)(
				CSSModules(
					Table,
					{ ...styles, ...this.props.classes },
					{
						allowMultiple: true
					}
				)
			)
		});
	}

	render() {
		const Elem = this.state.wrapped;
		return this.props.hasTable ? <Elem {...this.props} /> : null;
	}
}

export default connect(
	(state, props) => {
		const table = state.smartTable.tables[props.smartTableId];
		return {
			hasTable: !!table
		};
	},
	dispatch => ({
		registerTable: (tableId, tableHeadBody, rowsPerPage) =>
			dispatch(actions.registerTable(tableId, tableHeadBody, rowsPerPage))
	})
)(SmartTable);

function getValue(item, propPath) {
	const propName = propPath[0];
	if (propName) {
		const sub = item[propName];
		if (sub && propPath.length > 1) {
			return getValue(sub, propPath.slice(1));
		} else {
			return sub;
		}
	} else {
		return undefined;
	}
}

//helper class to map column headers to columns for responsive tables
//deals with colspan in headers or tds
class HeaderList {
	_colSpan = 0;
	th = null;
	_colsRemaining = 0;

	//advance the index if needed
	_Next() {
		if (this._colsRemaining <= 0) {
			//increment header index based on this cols' colspan
			this._currentIndex += this._colsRemaining * -1 + 1;

			if (this._currentIndex >= this._thList.length) {
				//wrap back to start
				this._currentIndex = 0;
			}
			this.th = this._thList[this._currentIndex];
			this._colSpan = this.th.colSpan;
			this._colsRemaining = this._colSpan;
		}
	}
	getHeader(colspan) {
		this._colsRemaining -= colspan;
		const returns = this.th;
		this._Next();
		return returns.th;
	}

	reset() {
		this._currentIndex = 0;
		this.th = this._thList[this._currentIndex];
		this._colSpan = this.th.colSpan;
		this._colsRemaining = this._colSpan;
	}

	add(th) {
		this._thList.push({ colSpan: parseInt(th.props.colSpan || 1, 10), th: th });
	}

	get length() {
		return this._thList.length;
	}

	_currentIndex = 0;
	_thList = [];
}

class TableDimension {
	pendingRowHeights = {};
	rowHeights = {};

	reset() {
		this.pendingRowHeights = {};
		this.rowHeights = {};
	}

	addHeight(item) {
		const existing = this.pendingRowHeights[item.key];

		if (existing) {
			if (existing < item.height) {
				this.pendingRowHeights[item.key] = item.height;
				return true;
			}
		} else if (item.height > 0) {
			this.pendingRowHeights[item.key] = item.height;
			return true;
		}
		return false;
	}

	next() {
		const any = Object.keys(this.pendingRowHeights).some(prop => {
			if (isNaN(this.rowHeights[prop])) {
				return true;
			} else if (this.rowHeights[prop] !== this.pendingRowHeights[prop]) {
				return true;
			} else {
				return false;
			}
		});
		this.rowHeights = this.pendingRowHeights;
		this.pendingRowHeights = {};
		return any;
	}
}



// WEBPACK FOOTER //
// ./src/components/SmartTable/SmartTable.js